import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import * as Bixby from '$/core';
import { DocumentsClientService, DocumentsAdminService } from '$/services';
import { toastError } from '$/store/toast/actions';
import { downloadFile } from '$/store/export/actions';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import { getIsCurrentUserDocHostingAdminState, getIsCurrentUserSuperAdminState } from '$/store/profile/selectors';
import './index.scss';
import DocumentAnnouncement from '$/components/DocumentAnnouncement';
import { orderBy } from '$/core/utils';
import CompanyDocumentGridTemplate from '$/components/company-documents-grid/company-document-grid-template';
import BixButton from '$/components/global/bixButton';
import Modal from '$/components/global/modal';
import { GridApi } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import 'react-tabs/style/react-tabs.css';

interface IDocumentsProps {
  currentCompany?: Bixby.Models.AdminCompany;
  dispatchDownloadDocument: (blob: Blob, fileName: string) => Promise<void>;
  dispatchDownloadFailure: () => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
  isSuperAdmin: boolean;
  isDocHostingAdmin: boolean;
}

const AdminCompanyDocumentGrids: React.FC<IDocumentsProps> = props => {
  const { currentCompany, dispatchToastError } = props;
  const [documents, setDocuments] = useState<Bixby.Models.CompanyDocument[]>();
  const [documentsWithSuggestedTags, setDocumentsWithSuggestedTags] =
    useState<Bixby.Models.CompanyDocumentSuggestedTag[]>();
  const [suggestTagModel, setSuggestTagModel] = useState(false);
  const [submittingSuggestedTagSuccess, setSubmittingSuggestedTagSucess] = useState(false);
  const [submittingSuggestedTagError, setSubmittingSuggestedTagError] = useState(undefined as string | undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isSubmittingTags, setIsSubmittingTags] = useState(false);
  const [documentsError, setDocumentsError] = useState(undefined as string | undefined);
  const [selectedRowCount, setSelectedRowCount] = useState(0);

  const [gridApi, setGridApi] = useState(null as GridApi | null);
  const [companyDocumentCategories, setCompanyDocumentCategories] = useState<Bixby.Models.CompanyDocumentCategory[]>(
    []
  );

  const publishedDocuments = useMemo<Bixby.Models.CompanyDocument[]>(() => {
    if (!documents) return [];
    const docs = documents.filter(_ => _.state === Bixby.Enums.CompanyDocumentState.Published);
    return docs;
  }, [documents]);

  const deletedDocuments = useMemo<Bixby.Models.CompanyDocument[]>(() => {
    if (!documents) return [];
    const docs = documents.filter(_ => _.state === Bixby.Enums.CompanyDocumentState.Deleted);
    return docs;
  }, [documents]);

  const errorDocuments = useMemo(() => {
    if (!documents) return [];
    const docs = documents.filter(_ => _.state === Bixby.Enums.CompanyDocumentState.Error);
    return docs;
  }, [documents]);

  const requiresApprovalDocuments = useMemo<Bixby.Models.CompanyDocument[]>(() => {
    if (!documents) return [];
    const docs = documents.filter(_ => _.state === Bixby.Enums.CompanyDocumentState.PendingRequiresApproval);
    const orderedDocs: Bixby.Models.CompanyDocument[] = Bixby.Utils.orderBy(docs, _ => _.client_Name);
    return orderedDocs;
  }, [documents]);

  const groupedDocumentsSummary = useMemo<{ client: string; count: number; active: boolean }[]>(() => {
    if (!documents) return [];
    const groupedDocuments: any = {};
    const documentsNoError = documents.filter(
      _ => _.state !== Bixby.Enums.CompanyDocumentState.Error && _.state !== Bixby.Enums.CompanyDocumentState.Deleted
    );

    const associatedClients = currentCompany?.clients;
    associatedClients?.forEach(client => {
      if (!groupedDocuments[client.client_Name]) {
        groupedDocuments[client.client_Name] = [];
      }
    });

    documentsNoError.forEach(document => {
      const client_Name = document.client_Name ?? 'No Client';
      if (!groupedDocuments[client_Name]) {
        groupedDocuments[client_Name] = [];
      }
      groupedDocuments[client_Name].push(document);
    });
    const summary: { client: string; count: number; active: boolean }[] = [];

    for (const client in groupedDocuments) {
      summary.push({
        client,
        count: groupedDocuments[client].length,
        active: groupedDocuments[client]?.[0]?.clientCompany_IsActive,
      });
    }
    return orderBy(
      summary,
      [_ => _.active === false, _ => _.client === 'No Client', _ => _.count],
      ['asc', 'asc', 'desc']
    );
  }, [currentCompany?.clients, documents]);

  const uniqueDocumentsCount = useMemo(() => {
    if (!documents) return [];
    const clientDocs: { client: string; count: number; active: boolean }[] = orderBy(
      groupedDocumentsSummary.filter(_ => _.client !== 'No Client' && _.count !== 0 && _.active !== false),
      _ => _.count,
      'asc'
    );
    const noClientDocs: { client: string; count: number }[] = groupedDocumentsSummary.filter(
      _ => _.client === 'No Client'
    );
    const lowestClientCount = clientDocs && clientDocs.length >= 1 ? clientDocs[0].count : 0;
    const noClientCount = noClientDocs && noClientDocs.length ? noClientDocs[0].count : 0;
    const totalCount = lowestClientCount + noClientCount;
    return totalCount;
  }, [documents, groupedDocumentsSummary]);

  const getDocuments = useCallback(async () => {
    if (!currentCompany) return;

    setIsLoading(true);
    setDocumentsError(undefined);
    try {
      const [documents, companyDocumentCategories] = await Promise.all([
        DocumentsAdminService.getDocumentsByCompanyId(currentCompany.id),
        DocumentsClientService.getDocumentCategories(),
      ]);
      setCompanyDocumentCategories(companyDocumentCategories);
      setDocuments(orderBy(documents, _ => _.publishedDate, ['desc']));
    } catch (e: any) {
      setDocumentsError('Error retrieving documents');
    } finally {
      setIsLoading(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    if (currentCompany) {
      getDocuments();
    }
  }, [currentCompany, getDocuments]);

  const handleApplySuggestedTagsOpen = async () => {
    setIsLoadingTags(true);
    try {
      if (currentCompany?.id) {
        const result = await DocumentsAdminService.getSuggetedTagsByCompanyId(currentCompany?.id);
        setDocumentsWithSuggestedTags(result || []);
        setSuggestTagModel(true);
      }
    } catch (e: any) {
      dispatchToastError('Error gettings documents');
    } finally {
      setIsLoadingTags(false);
    }
  };

  const handleApplySuggestedTagsSubmit = async () => {
    const selectedRows = gridApi?.getSelectedRows();
    if (!selectedRows || selectedRows.length === 0) return;

    setSelectedRowCount(selectedRows.length);
    try {
      setIsSubmittingTags(true);
      await DocumentsAdminService.submitSuggestedTags(selectedRows);
      setSubmittingSuggestedTagSucess(true);
      getDocuments();
    } catch (err: any) {
      setSubmittingSuggestedTagError(err);
    } finally {
      setIsSubmittingTags(false);
    }
  };

  const formatCompanyDocumentCompanyDocumentCategory = (
    categoryName: string,
    financialDocumentType?: string,
    financialDocumentDate?: Date,
    orgChartPageNumber?: number
  ) => {
    if (financialDocumentType === Bixby.Enums.FinancialDocumentType.Q) {
      let month = dayjs.utc(financialDocumentDate).month();
      month += 1;
      let year = dayjs.utc(financialDocumentDate).year();
      if (currentCompany?.fiscalYearEnd && currentCompany?.fiscalYearEnd !== null) {
        const fyeMonth = dayjs.utc(currentCompany?.fiscalYearEnd).month() + 1;
        const fyeAdjustment = 12 - fyeMonth;
        if (month + fyeAdjustment > 12) {
          month = month + fyeAdjustment - 12;
          year = year + 1;
        } else {
          month = month + fyeAdjustment;
        }
      }
      const quarter = month < 4 ? '1' : month >= 4 && month < 7 ? '2' : month >= 7 && month < 10 ? '3' : '4';
      return ` ${categoryName} (${financialDocumentType}${quarter}-${year.toString().substring(2, 4)} - ${dayjs
        .utc(financialDocumentDate)
        .format('MMM-YY')})`;
    } else if (financialDocumentType === Bixby.Enums.FinancialDocumentType.FYE) {
      return ` ${categoryName} (${financialDocumentType} - ${dayjs.utc(financialDocumentDate).format('MMM-YY')})`;
    } else if (orgChartPageNumber) {
      return ` ${categoryName} (Page ${orgChartPageNumber})`;
    } else {
      return categoryName;
    }
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  const handleApplySuggestedTagsClose = () => {
    setDocumentsWithSuggestedTags(undefined);
    setSuggestTagModel(false);
    setSubmittingSuggestedTagSucess(false);
    setSubmittingSuggestedTagError(undefined);
    setSelectedRowCount(0);
  };

  if (!Bixby.Utils.isNullOrEmpty(documentsError)) {
    return <p className="error">{documentsError}</p>;
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <>
        <DocumentAnnouncement />
        {documents && !Bixby.Utils.isNullOrEmpty(documents) ? (
          <div className="documents-data-grid">
            <>
              <h3>Documents Breakdown</h3>
              <dl className="documents-summary">
                {groupedDocumentsSummary &&
                  groupedDocumentsSummary.length > 0 &&
                  groupedDocumentsSummary.map((summary, index) => (
                    <React.Fragment key={index}>
                      <dt className={summary.active === false ? 'inactive' : ''}>
                        {summary.client} {summary.active === false ? '(Not Active)' : ''}{' '}
                      </dt>
                      <dd>{summary.count}</dd>
                    </React.Fragment>
                  ))}
                <dt>Error</dt>
                <dd>{errorDocuments.length}</dd>
                <dt>Deleted</dt>
                <dd>{deletedDocuments.length}</dd>
                <div className="total">{uniqueDocumentsCount}</div>
              </dl>
            </>
            <BixButton type="button" onClick={handleApplySuggestedTagsOpen} isLoading={isLoadingTags}>
              Get Suggested Tagging
            </BixButton>
            <CompanyDocumentGridTemplate
              documents={requiresApprovalDocuments}
              companyDocumentCategories={companyDocumentCategories}
              isPendingApprovalGrid={true}
              getDocuments={getDocuments}
            />
            <CompanyDocumentGridTemplate
              documents={publishedDocuments}
              companyDocumentCategories={companyDocumentCategories}
              isPublishedGrid={true}
              getDocuments={getDocuments}
            />
            <CompanyDocumentGridTemplate
              documents={errorDocuments}
              companyDocumentCategories={companyDocumentCategories}
              isErrorGrid={true}
              getDocuments={getDocuments}
            />
            <CompanyDocumentGridTemplate
              documents={deletedDocuments}
              companyDocumentCategories={companyDocumentCategories}
              isDeleteGrid={true}
              getDocuments={getDocuments}
            />
          </div>
        ) : (
          <NoDataFound dataType="documents" />
        )}
        {suggestTagModel && (
          <Modal active={true} hideClose title="Confirm Suggested Tags" wideView={true}>
            {documentsWithSuggestedTags && documentsWithSuggestedTags.length > 0 && !submittingSuggestedTagSuccess && (
              <>
                <div className="documents-suggested-tag-grid">
                  <div className="grid_container">
                    <div className="grid ag-theme-alpine">
                      <AgGridReact
                        defaultColDef={{
                          flex: 1,
                          resizable: true,
                          lockPinned: true,
                          cellClass: 'align-left',
                          sortable: true,
                        }}
                        rowData={documentsWithSuggestedTags}
                        suppressDragLeaveHidesColumns={true}
                        suppressMovableColumns={true}
                        rowHeight={36}
                        headerHeight={36}
                        onGridReady={onGridReady}
                        rowSelection="multiple"
                        columnDefs={[
                          { field: 'id', headerName: 'id', hide: true, valueGetter: 'node.id' },
                          {
                            headerName: 'Published Date',
                            field: 'publishedDate',
                            maxWidth: 150,
                            filter: 'agDateColumnFilter',
                            cellRenderer: (params: any) => {
                              return dayjs.utc(params.value).format('MM/DD/YYYY');
                            },
                          },
                          { headerName: 'Title', field: 'title', maxWidth: 400 },
                          { headerName: 'Client Name', field: 'client_Name', maxWidth: 150 },
                          {
                            field: 'suggestedTag',
                            headerName: 'Type',
                            cellRenderer: (event: any) => {
                              return event.value
                                .map((cdcdc: Bixby.Models.CompanyDocumentCompanyDocumentCategory) =>
                                  formatCompanyDocumentCompanyDocumentCategory(
                                    cdcdc.categoryName,
                                    cdcdc.financialDocumentType,
                                    cdcdc.financialDocumentDate,
                                    cdcdc.orgChartPageNumber
                                  )
                                )
                                .join('; ');
                            },
                            maxWidth: 250,
                          },
                          { headerName: 'Description', field: 'description', maxWidth: 150 },
                          { headerName: 'Source', field: 'suggestionSource', maxWidth: 150 },
                          {
                            headerName: 'Confirm',
                            checkboxSelection: true,
                            headerCheckboxSelection: true,
                            maxWidth: 150,
                          },
                        ]}></AgGridReact>
                    </div>
                  </div>
                </div>
                <div className="modal_button_container">
                  <BixButton
                    type="button"
                    onClick={handleApplySuggestedTagsSubmit}
                    useLoading
                    isLoading={isSubmittingTags}
                    disabled={isSubmittingTags}>
                    Confirm
                  </BixButton>
                  <BixButton
                    light
                    type="button"
                    onClick={() => handleApplySuggestedTagsClose()}
                    disabled={isSubmittingTags}>
                    Cancel
                  </BixButton>
                </div>
              </>
            )}
            {(documentsWithSuggestedTags == undefined || documentsWithSuggestedTags.length == 0) && (
              <div className="modal_button_container">
                <p>No suggested tags found.</p>
                <BixButton light type="button" onClick={() => handleApplySuggestedTagsClose()}>
                  Cancel
                </BixButton>
              </div>
            )}
            {submittingSuggestedTagError && <p className="error_msg">{submittingSuggestedTagError}</p>}
            {submittingSuggestedTagSuccess && documentsWithSuggestedTags && (
              <>
                <p>
                  Successfully tagged {selectedRowCount} {Bixby.Utils.pluralizeStr(selectedRowCount, 'document')}
                </p>
                <BixButton onClick={handleApplySuggestedTagsClose}>Close</BixButton>
              </>
            )}
          </Modal>
        )}
      </>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  const { isDocHostingAdmin } = getIsCurrentUserDocHostingAdminState(state);
  return {
    isSuperAdmin,
    isDocHostingAdmin,
    currentCompany: state.companyState.currentCompany,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchDownloadDocument: (blob: Blob, fileName: string) => dispatch(downloadFile(blob, fileName)),
  dispatchDownloadFailure: () => dispatch(toastError('Failed to download document')),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminCompanyDocumentGrids);
